<template>
    <div
        :id="['hotspot-' + (index + 1)]"
        class="hotspot-element"
        :style="{
            left: left + '%',
            top: top + '%',
            height: height + '%',
            width: width + '%',
        }"
        v-if="visible"
        @click.stop="interactionEvent"
    >
        <div class="tag-inner-box">
            <div v-html="content"
                 :style="contentStyle"/>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { debounce } from 'lodash';
import { usePlayerStore } from '@/playerVue3/storage/storePlayer';
import { HOTSPOT_TYPE } from '@/playerVue3/enums/hotspot-enum';

// INIATIALIZE STORE & COMPOSABLES
const store = usePlayerStore();

// PROPS & EMITS
const props = defineProps({
    hotspot: {
        type: Object,
        required: true,
    },
    index: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['autoEventByTime', 'interactionEvent']);

// REACTIVE VARIABLES
const wasFired = ref(false);

// COMPUTED PROPERTIES
const content = computed(() => {
    const type = props.hotspot.hotspotType.hotspotTypeId;
    if (type == HOTSPOT_TYPE.TEXT_VARIABLE || type == HOTSPOT_TYPE.IMAGE_VARIABLE) {
        return replaceDataVariableInHotspot(props.hotspot.variableField);
    }
    if (props.hotspot.hotspotDesign !== null) {
        if (props.hotspot.hotspotDesign.version === 2) {
            const configurationData = JSON.parse(props.hotspot.hotspotDesign.configurationData);
            if (configurationData != null && configurationData.colorHotspotDesignCustom == 'Invisible') {
                props.hotspot.codeDesign = '';
            } else {
                let propertyConfigurationData = '';
                if (props.hotspot.configurationData != null) {
                    propertyConfigurationData = JSON.parse(props.hotspot.configurationData);
                } else {
                    propertyConfigurationData = configurationData;
                }
                props.hotspot.codeDesign = replaceVariablesInDesign(props.hotspot.hotspotDesign.codeHtml, propertyConfigurationData);
            }
        } else {
            props.hotspot.codeDesign = '<style>' + props.hotspot.hotspotDesign.codeCss + '</style>' + props.hotspot.hotspotDesign.codeHtml;
        }
    }
    return props.hotspot.codeDesign;
});

const contentStyle = computed(() => {
    let contStyle = '';
    if (props.hotspot.version === 2) {
        contStyle = "width:100%;height:100%";
    }
    return contStyle;
});

const endTime = computed(() => {
    if (props.hotspot.timeDuration < 0.25) {
        return Number(props.hotspot.timeStart) + 0.25;
    }
    return props.hotspot.timeEnd;
});

const height = computed(() => {
    return props.hotspot.hotspotHeight;
});

const left = computed(() => {
    if (props.hotspot.version !== 2 && props.hotspot.hotspotType.hotspotTypeId === HOTSPOT_TYPE.TARGET) {
        return ((props.hotspot.xLocation * 100) - (width.value / 2)).toFixed(2);
    }
    return (props.hotspot.xLocation * 100).toFixed(2);
});

const top = computed(() => {
    if (props.hotspot.version !== 2 && props.hotspot.hotspotType.hotspotTypeId === HOTSPOT_TYPE.TARGET) {
        return ((props.hotspot.yLocation * 100) - (height.value / 2)).toFixed(2);
    }
    return (props.hotspot.yLocation * 100).toFixed(2);
});

const visible = computed(() => {
    if (store.getVideoWasExecuted) {
        const videoMetrics = store.getPlayerMetrics;
        if (videoMetrics.currentTimeBySeconds > 0 && videoMetrics.currentTimeBySeconds >= Number(props.hotspot.timeStart) && videoMetrics.currentTimeBySeconds <= Number(endTime.value)) {
            fireEvent(true);
            props.hotspot.visible = true;
            return true;
        } else if (wasFired.value) {
            fireEvent(false);
            props.hotspot.visible = false;
        }
    }
    return false;
});

const width = computed(() => {
    return props.hotspot.hotspotWidth;
});

// METHODS
const autoEventByTime = debounce(function () {
    emit('autoEventByTime', props.hotspot);
}, 1000, {
    leading: true,
    trailing: false,
});

const interactionEvent = debounce(function() {
    emit('interactionEvent', props.hotspot);
}, 1000, {
    leading: true,
    trailing: false,
});

const fireEvent = (visible) => {
    if (visible && !wasFired.value) {
        wasFired.value = true;
        autoEventByTime();
    } else if (!visible) {
        wasFired.value = false;
    }
};

const replaceVariablesInDesign = (itemHTML, propertyConfigurationData) => {
    Object.entries(propertyConfigurationData).map(function(entry){
        let key = entry[0];
        let value = entry[1];
        let variableToReplace = "%%" + key + "%%";
        const varRegExp = new RegExp(variableToReplace, 'g');
        itemHTML = itemHTML.replace(varRegExp, value);
    });
    return itemHTML;
};

const isValidUrl = (string) => {
    if (string && string.includes('mailto')) {
        return true;
    } else {
        if (string && string.length > 1 && string.slice(0, 2) == '//') {
            string = 'http:' + string;
        }
        try {
            let url = new URL(string);
            return url.hostname && url.hostname.match(/^([a-z0-9])(([a-z0-9-]{1,61})?[a-z0-9]{1})?(\.[a-z0-9](([a-z0-9-]{1,61})?[a-z0-9]{1})?)?(\.[a-zA-Z]{2,4})+$/) ? true : false;
        } catch (_) {
            return false;
        }
    }
};

const replaceDataVariableInHotspot = (itemHTML) => {
    const path = store.getPathLocalOrServer;
    const varRegExp = new RegExp(/%%(.*?)%%/g);
    const variables = itemHTML.match(varRegExp);
    if (variables) {
        variables.forEach( (variable) => {
            const varRegExp2 = new RegExp(/%%(.*?)%%/g);
            const variableName = variable.match(varRegExp2)[0].replace(/%/g, '');
            if (variableName !== 'animation') {
                const variableValue = store.getDataVariableByVariableName(variableName);
                if (variableValue.length > 0) {
                    const foundImage = variableValue[0].fieldExcel.description.search("image_variable");
                    if (foundImage > -1 && variableValue[0].path != null) {
                        itemHTML = itemHTML.replace(variable, path + 'images_variables/' + variableValue[0].path);
                    } else if (foundImage > -1 && variableValue[0].path === null) {
                        itemHTML = itemHTML.replace(variable, path + 'images_variables/hotspot-image-referente.jpg');
                    } else {
                        itemHTML = itemHTML.replace(variable, variableValue[0].description);
                    }
                } else if (props.hotspot.imageReference !== null && props.hotspot.imageReference !== '' && props.hotspot.imageReference !== undefined && isValidUrl(props.hotspot.imageReference)) {
                    itemHTML = itemHTML.replace(variable, props.hotspot.imageReference);
                } else if (props.hotspot.imageReference !== null && props.hotspot.imageReference !== '' && props.hotspot.imageReference !== undefined) {
                    itemHTML = itemHTML.replace(variable, path + 'images_variables/' + props.hotspot.imageReference);
                } else {
                    itemHTML = itemHTML.replace(variable, '');
                }
            } else {
                if (props.hotspot.hotspotAnimationIn !== '') {
                    itemHTML = itemHTML.replace(variable, props.hotspot.hotspotAnimationIn);
                } else {
                    itemHTML = itemHTML.replace(variable, '');
                }
            }
        });
    }

    return itemHTML;
};

// LIFECYCLE HOOKS
</script>
